import { DatePicker, Input, Select, TextArea } from "common/InputForm";
import imgUploadToCloudFun from "components/modals/imgUploadAndGetUrl";
import useButtonLoader from "hooks/useButtonLoader";
import React, { useEffect, useState } from "react";
import { IoChevronBack } from "react-icons/io5";
import { RiArrowLeftSLine } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { categoryListApi } from "services/category";
import {
  addOfferApi,
  fetchOfferDetailsApi,
  updateOfferApi,
} from "services/offer";
import { subCategoryListApi } from "services/subCategory";
import { productListApi } from "services/product-management";
import { toUtcEndOfDay, toUtcStartOfDay } from "utils/dateFormat";
import { ImagePreview } from "utils/imagePreviewURLConverter";
import { toastMessage } from "utils/toastMessage";

const APPLY_TO = {
  ALL: "ALL_PRODUCTS",
  SELECTED: "SELECTED_PRODUCTS",
};

const AddOffer = ({ type = "", offerId = "" }) => {
  const [error, setError] = useState({});
  const [category, setCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const isUpdated = type === "update";
  const [buttonLoader, setButtonLoader] = useButtonLoader(
    isUpdated ? "Update" : "Add",
  );

  const navigate = useNavigate();
  const [input, setInput] = useState({
    category: "",
    subcategory: "",
    image: "",
    title: "",
    description: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    appliesTo: APPLY_TO.ALL,
    products: [],
    startDate: "",
    endDate: "",
  });

  // console.log({ input, type, isUpdated, offerId, category })
  // -----------handle-Date-change---------------
  function handleDateChange(name, date) {
    setInput({ ...input, [name]: date });
  }
  // -----------handle-onchange----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    const noLeadSpace = value.replace(/^\s+/, ""); // Remove leading spaces

    if (name === "discountValue") {
      // ✅ Only Numbers Allowed, No Spaces
      const numberValue = noLeadSpace.replace(/[^0-9]/g, "");
      setInput({ ...input, [name]: numberValue });
      return;
    }

    // Sub category list depends on the selected category, so any previously
    // picked sub category becomes invalid the moment the category changes.
    // The product picks are scoped to the category/subcategory too, so reset
    // them whenever the scope changes.
    if (name === "category") {
      setInput({ ...input, category: value, subcategory: "", products: [] });
      return;
    }
    if (name === "subcategory") {
      setInput({ ...input, subcategory: value, products: [] });
      return;
    }
    // Switching back to "all products" drops any hand-picked selection.
    if (name === "appliesTo") {
      setInput({
        ...input,
        appliesTo: value,
        products: value === APPLY_TO.ALL ? [] : input.products,
      });
      return;
    }
    // ✅ For other fields, allow text without leading space
    setInput({ ...input, [name]: noLeadSpace });
  };

  // -----------toggle a product in the selected list----------------
  const toggleProduct = (productId) => {
    setInput((prev) => {
      const selected = prev.products.includes(productId)
        ? prev.products.filter((id) => id !== productId)
        : [...prev.products, productId];
      return { ...prev, products: selected };
    });
  };

  // -----------select all / clear all products----------------
  const selectAllProducts = () => {
    setInput((prev) => ({
      ...prev,
      products: productsList.map((p) => p._id),
    }));
  };
  const clearAllProducts = () => {
    setInput((prev) => ({ ...prev, products: [] }));
  };

  // ---------handle-image-change-------------------
  const imgUploadFunction = async (e) => {
    const file = e?.target?.files?.[0];
    try {
      const data = await imgUploadToCloudFun(file);
      if (data?.status === 200) {
        setInput((prev) => ({
          ...prev,
          image: data.data[0], // cloud URL
          previewImage: file, // for local preview only
        }));
      }
    } catch (error) {
      console.log("error while uploading and getting data", error);
    }
    // }
  };

  // ---------get-display-file-name-------------------
  // Native <input type="file"> can never be programmatically set to show
  // a filename (browser security restriction), so it always displays
  // "No file chosen" until the user manually picks a file. To show the
  // correct filename in both add (freshly picked file) and edit
  // (existing image URL from API) cases, we compute our own label here
  // instead of relying on the native input's text.
  const getImageFileName = () => {
    if (input?.previewImage?.name) return input.previewImage.name; // freshly picked file
    if (input?.image) {
      try {
        const url = new URL(input.image);
        return decodeURIComponent(url.pathname.split("/").pop());
      } catch {
        return input.image.split("/").pop();
      }
    }
    return "";
  };

  // ------------validation-----------------
  const validation = () => {
    let errorMessage = {};
    let status = true;

    if (!input.category) {
      errorMessage["category"] = "Please Select category";
      status = false;
    }

    // Only enforce when the chosen category actually has sub categories,
    // so categories without any are still submittable.
    if (input.category && subCategory.length > 0 && !input.subcategory) {
      errorMessage["subcategory"] = "Please Select sub category";
      status = false;
    }

    if (!input.title) {
      errorMessage["title"] = "Please enter offer name";
      status = false;
    }

    if (!input.description) {
      errorMessage["description"] = "Please enter description";
      status = false;
    }

    if (!input.startDate) {
      errorMessage["startDate"] = "Please select start date";
      status = false;
    }

    if (!input.endDate) {
      errorMessage["endDate"] = "Please select end date";
      status = false;
    }

    if (!input.discountValue) {
      errorMessage["discountValue"] = "Please enter discount percentage";
      status = false;
    }

    if (
      input.appliesTo === APPLY_TO.SELECTED &&
      (!input.products || input.products.length === 0)
    ) {
      errorMessage["products"] = "Please select at least one product";
      status = false;
    }

    if (!input.image) {
      errorMessage["image"] = "Please choose image";
      status = false;
    }

    setError(errorMessage);
    return status;
  };
  // ----------fetch-category---------------
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        // Without an explicit limit the API returns only its default first
        // page, so the dropdown silently dropped the rest of the categories.
        // ACTIVE only, to match the sub category dropdown below.
        const query = { type: "CATEGORY", status: "ACTIVE", limit: 1000 };
        const {
          data: {
            data: { total, categories },
          },
          status,
          message,
        } = await categoryListApi(query);
        if (status === 200) {
          setCategory(categories);
        }
      } catch (error) {
        console.log("Cat Data fetch error:---> ", error);
      }
    };
    fetchCategory();
  }, []);
  // ----------fetch-sub-category-of-selected-category---------------
  useEffect(() => {
    if (!input?.category) {
      setSubCategory([]);
      return;
    }
    const fetchSubCategory = async () => {
      try {
        const query = {
          status: "ACTIVE",
          limit: 1000,
          category: [input.category],
        };
        const {
          data: { status, code, data },
        } = await subCategoryListApi(query);
        if (status || code === 200) {
          setSubCategory(data?.data || []);
        }
      } catch (error) {
        console.log("Sub Cat Data fetch error:---> ", error);
        setSubCategory([]);
      }
    };
    fetchSubCategory();
  }, [input?.category]);
  // ----------fetch-products-for-picker (only when selecting specific ones)-----
  useEffect(() => {
    if (input?.appliesTo !== APPLY_TO.SELECTED || !input?.category) {
      setProductsList([]);
      return;
    }
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const query = {
          limit: 1000,
          category: input.category,
        };
        if (input.subcategory) query.subcategory = input.subcategory;
        const {
          data: { code, data },
        } = await productListApi(query);
        if (code === 200) {
          setProductsList(data?.data || []);
        } else {
          setProductsList([]);
        }
      } catch (error) {
        console.log("Product Data fetch error:---> ", error);
        setProductsList([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [input?.appliesTo, input?.category, input?.subcategory]);
  // ----------fetch-single-details---------------
  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        const res = await fetchOfferDetailsApi(offerId);
        const { code, data } = res?.data;
        if (code === 200) {
          const formattedData = {
            category: data?.category?._id || "",
            subcategory: data?.subcategory?._id || data?.subcategory || "",
            image: data?.image || "",
            title: data?.title || "",
            description: data?.description || "",
            discountType: data?.discountType || "PERCENTAGE",
            discountValue: data?.discountValue || "",
            appliesTo: data?.appliesTo || APPLY_TO.ALL,
            products: Array.isArray(data?.products)
              ? data.products.map((p) => p?._id || p)
              : [],
            startDate: data?.startDate || "",
            endDate: data?.endDate || "",
          };
          setInput(formattedData);
        }
      } catch (error) {
        console.log("Cat Data fetch error:---> ", error);
      }
    };
    if (offerId) {
      fetchOfferDetails();
    }
  }, []);
  // -------------Add-and--update-offer---------------------
  const handleSubmit = async (e) => {
    if (validation()) {
      try {
        setButtonLoader(true);
        const { previewImage, image, startDate, endDate, ...rest } = input;
        const payload = {
          ...rest,
          startDate: toUtcStartOfDay(startDate),
          endDate: toUtcEndOfDay(endDate),
        };
        if (previewImage && previewImage.name) {
          payload.image = image;
        }
        const res = isUpdated
          ? await updateOfferApi(offerId, payload)
          : await addOfferApi(payload);
        const { code, message } = res?.data;
        if (code === 200) {
          toastMessage(message, "success", "addPromoCode");
          navigate(-1);
        }
      } catch (error) {
        console.error("Upload Error:", error);
      } finally {
        setButtonLoader(false);
      }
    }
  };

  return (
    <div className="add-cat-parent-container">
      <div className="link-header-holder">
        <p>
          <Link to={-1}>
            <IoChevronBack />
            Back
          </Link>
          <span>Offer Management</span>
          &nbsp;
          <span>/ {isUpdated ? "Update Offer" : "Add Offer"}</span>
        </p>
      </div>
      <div className="sub_title has_button">
        <Link to={-1}>
          <RiArrowLeftSLine />
        </Link>{" "}
        <h3>{isUpdated ? "Update Offer" : "Add Offer"}</h3>
      </div>

      <div className="card">
        <div className="form_field_wrap">
          <div className="form_field">
            <Select
              type="text"
              label="Category"
              name="category"
              value={input?.category}
              onChange={handleChange}
              error={error.category}
            >
              <option value="">select category</option>
              {category.length > 0 ? (
                category.map((data, ind) => (
                  <option key={ind} value={data?._id}>
                    {data?.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No category found
                </option>
              )}
              <option value=""></option>
            </Select>
          </div>
          <div className="form_field">
            <Select
              type="text"
              label="Sub Category"
              name="subcategory"
              value={input?.subcategory}
              onChange={handleChange}
              error={error.subcategory}
              disabled={!input?.category}
            >
              <option value="">select sub category</option>
              {subCategory.length > 0 ? (
                subCategory.map((data, ind) => (
                  <option key={ind} value={data?._id}>
                    {data?.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  {input?.category
                    ? "No sub category found"
                    : "Select category first"}
                </option>
              )}
            </Select>
          </div>
          <div className="form_field">
            <Input
              type="text"
              label="Offer Name"
              placeholder="Enter offer name"
              name="title"
              value={input?.title}
              onChange={handleChange}
              error={error.title}
            />
          </div>
          <div className="form_field">
            <Input
              type="text"
              label="Discount (%)"
              placeholder="Enter discount percentage (1-100)"
              name="discountValue"
              value={input?.discountValue}
              onChange={handleChange}
              error={error.discountValue}
              maxLength={3}
            />
          </div>
        </div>

        <div className="form_field_wrap">
          <div className="form_field">
            <Select
              type="text"
              label="Apply To"
              name="appliesTo"
              value={input?.appliesTo}
              onChange={handleChange}
              error={error.appliesTo}
            >
              <option value={APPLY_TO.ALL}>
                All products in category
              </option>
              <option value={APPLY_TO.SELECTED}>Selected products</option>
            </Select>
          </div>
        </div>

        {input?.appliesTo === APPLY_TO.SELECTED && (
          <div className="form_field_wrap">
            <div className="form_field" style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <label className="input_label" style={{ marginBottom: 0 }}>
                  Select Products
                  {input?.products?.length > 0
                    ? ` (${input.products.length} selected)`
                    : ""}
                </label>
                {productsList.length > 0 && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="button"
                      style={{ padding: "4px 12px", fontSize: 13 }}
                      onClick={selectAllProducts}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="button button-outline"
                      style={{ padding: "4px 12px", fontSize: 13 }}
                      onClick={clearAllProducts}
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
              {error?.products && (
                <p className="error_message">{error.products}</p>
              )}
              {productsLoading ? (
                <p>Loading products…</p>
              ) : productsList.length === 0 ? (
                <p>
                  {input?.category
                    ? "No products found for this selection"
                    : "Select a category first"}
                </p>
              ) : (
                <div
                  className="offer_product_picker"
                  style={{
                    maxHeight: 320,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    padding: 8,
                  }}
                >
                  {productsList.map((product) => (
                    <label
                      key={product._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "6px 4px",
                        borderBottom: "1px solid #f2f2f2",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <input
                          type="checkbox"
                          checked={input.products.includes(product._id)}
                          onChange={() => toggleProduct(product._id)}
                        />
                        <span>{product?.name}</span>
                      </span>
                      <span style={{ color: "#737B8B", fontSize: 13 }}>
                        Current: {product?.effectiveDiscount || 0}%
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form_field_wrap">
          <div className="form_field">
            <TextArea
              label="Description"
              placeholder="Enter description"
              name="description"
              maxLength={250}
              value={input?.description}
              onChange={handleChange}
              error={error?.description}
            />
          </div>
        </div>
        <div className="form_field">
          <div className="file_input_wrap">
            <Input
              type="file"
              label="Image"
              name="image"
              accept="image/*"
              // onChange={handleImgChange}
              onChange={imgUploadFunction}
              error={error?.image}
            />

            {/* Custom filename label - overlaid on top of the native
                "No file chosen" text, in the same row as the Choose file
                button. Shown for both freshly picked files (add case) and
                existing images loaded from API (edit case), since the
                native file input can't display this itself */}
            {getImageFileName() && (
              <span className="selected-file-name">
                {getImageFileName()}
              </span>
            )}
          </div>

          {input?.image && (
            <div className="image_preview w-25 h-25">
              <img
                src={
                  input?.previewImage?.name
                    ? URL.createObjectURL(input.previewImage)
                    : input?.image
                }
                alt="Preview"
              />
            </div>
          )}
        </div>

        <div className="form_field_wrap">
          <div className="form_field">
            <DatePicker
              name="startDate"
              label="Start Date"
              selected={input?.startDate}
              placeholder="Start Date"
              onChange={(date) => handleDateChange("startDate", date)}
              maxDate={input?.endDate}
              minDate={new Date()}
              error={error.startDate}
            />
          </div>
          <div className="form_field">
            <DatePicker
              name="endDate"
              label="End Date"
              selected={input?.endDate}
              placeholder="End Date"
              onChange={(date) => handleDateChange("endDate", date)}
              minDate={input?.startDate || new Date()}
              error={error.endDate}
            />
          </div>
          <div className="form_field mt-2">
            <div className="submit-button-holder mt-4">
              <button
                type="button mt-1"
                className="button"
                ref={buttonLoader}
                onClick={handleSubmit}
              >
                {isUpdated ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOffer;